<template>
  <div class="kpi-admin">
    <div class="box">
      <h2 class="title is-4">
        Statistiques client
      </h2>

      <div class="field">
        <label class="label">Email du store</label>
        <div class="control">
          <input v-model="storeEmail" class="input" type="email" placeholder="Entrez l'email du store">
        </div>
      </div>

      <div class="field">
        <div class="control is-flex is-justify-content-flex-end">
          <button @click="fetchKpiData" class="button is-primary" :class="{'is-loading': loading}">
            Charger les statistiques
          </button>
        </div>
      </div>

      <div v-if="error" class="notification is-danger">
        {{ error }}
      </div>

      <div v-if="kpiData" class="content">
        <div class="columns is-multiline">
          <!-- Statistiques des formations par statut -->
          <div class="column is-6">
            <div class="box">
              <h3 class="title is-5">
                Statistiques des formations par statut
              </h3>
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Nombre</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="stat in kpiData.trainingStatusStats" :key="stat.status">
                    <td>{{ stat.status }}</td>
                    <td>{{ stat.total }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Statistiques de progression des formations -->
          <div class="column is-12">
            <div class="box">
              <h3 class="title is-5">
                Statistiques de progression des formations
              </h3>
              <table class="table is-fullwidth">
                <thead>
                  <tr>
                    <th>Formation</th>
                    <th>Apprenants ayant commencé</th>
                    <th>Apprenants ayant terminé</th>
                    <th>Taux de complétion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="stat in kpiData.trainingProgressStats" :key="stat.nom_formation">
                    <td>{{ stat.nom_formation }}</td>
                    <td>{{ stat.nb_apprenant_ont_commence_formation }}</td>
                    <td>{{ stat.nb_apprenant_ont_termine_formation }}</td>
                    <td>{{ stat.pourcentage_de_completude }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Nombre total d'étudiants ayant terminé -->
          <div class="column is-6">
            <div class="box">
              <h3 class="title is-5">
                Nombre total d'étudiants inscrits depuis le début
              </h3>
              <p class="has-text-centered is-size-3">
                {{ kpiData.studentsCount }}
              </p>
            </div>
            <div class="box mt-4">
              <h3 class="title is-5">
                Étudiants actifs ayant terminé une formation
              </h3>
              <p class="has-text-centered is-size-3">
                {{ kpiData.completedStudentsCount }}
              </p>
            </div>
          </div>

          <!-- Durée totale de formation -->
          <div class="column is-6">
            <div class="box">
              <h3 class="title is-5">
                Durée totale de formation
              </h3>
              <div class="content">
                <p><strong>En secondes :</strong> {{ kpiData.totalDuration.total_in_seconds }}</p>
                <p><strong>En minutes :</strong> {{ kpiData.totalDuration.total_in_minutes }}</p>
                <p><strong>En heures :</strong> {{ kpiData.totalDuration.total_in_hours }}</p>
                <p><strong>En jours :</strong> {{ kpiData.totalDuration.total_in_days }}</p>
              </div>
            </div>
            <div class="box mt-4">
              <h3 class="title is-5">
                ID client d'abonnement Stripe
              </h3>
              <p
                v-if="kpiData.stripeSubscriptionCustomerId"
                class="has-text-centered is-size-5 is-family-monospace break-words"
              >
                {{ kpiData.stripeSubscriptionCustomerId }}
              </p>
              <p v-else class="has-text-centered has-text-grey">
                Aucun
              </p>
            </div>
          </div>

          <!-- Statistiques des apprenants par statut -->
          <div class="column is-6">
            <div class="box">
              <h3 class="title is-5">
                Apprenants par statut
              </h3>
              <table v-if="kpiData.customerStatusStats" class="table is-fullwidth">
                <tbody>
                  <tr>
                    <td>Inscrits (étudiants actifs)</td>
                    <td class="has-text-right has-text-weight-bold">
                      {{ kpiData.customerStatusStats.students || 0 }}
                    </td>
                  </tr>
                  <tr>
                    <td>Bloqués</td>
                    <td class="has-text-right has-text-weight-bold">
                      {{ kpiData.customerStatusStats.blocked || 0 }}
                    </td>
                  </tr>
                  <tr>
                    <td>Paniers abandonnés</td>
                    <td class="has-text-right has-text-weight-bold">
                      {{ kpiData.customerStatusStats.abandoned_cart || 0 }}
                    </td>
                  </tr>
                  <tr>
                    <td>Retirés d'une formation</td>
                    <td class="has-text-right has-text-weight-bold">
                      {{ kpiData.customerStatusStats.removed_from_training || 0 }}
                    </td>
                  </tr>
                  <tr>
                    <td>Apprenants supprimés</td>
                    <td class="has-text-right has-text-weight-bold">
                      {{ kpiData.customerStatusStats.deleted_customers || 0 }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Dernière inscription -->
          <div class="column is-6">
            <div class="box">
              <h3 class="title is-5">
                Dernière inscription d'un apprenant
              </h3>
              <template v-if="kpiData.lastEnrollment">
                <p class="is-size-4 has-text-centered has-text-weight-bold mb-3">
                  {{ kpiData.lastEnrollment.enrolled_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}
                </p>
              </template>
              <p v-else class="has-text-centered has-text-grey">
                Aucune inscription
              </p>
            </div>
          </div>

          <!-- Statistiques des membres d'équipe -->
          <div class="column is-12">
            <div class="box">
              <h3 class="title is-5">
                Statistiques des membres d'équipe
              </h3>
              <table class="table is-fullwidth">
                <thead>
                <tr>
                  <th>Email</th>
                  <th>Prénom</th>
                  <th>Nom</th>
                  <th>Rôle</th>
                  <th>statut</th>
                  <th>Dernière connexion</th>
                  <th>Supprimé le</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="stat in kpiData.usersStats" :key="stat.id">
                  <td>{{ stat.email }}</td>
                  <td>{{ stat.firstname }}</td>
                  <td>{{ stat.lastname }}</td>
                  <td>{{ stat.role }}</td>
                  <td>{{ stat.status }}</td>
                  <td>{{ stat.last_login_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}</td>
                  <td>
                    <template v-if="stat.deleted_at">
                      {{ stat.deleted_at | momentFromUTC | moment('DD/MM/YY [à] HH:mm') }}
                    </template>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import APIAdmin from '@app/services/API/Admin';
import moment from '@shared/config/moment';

export default {
  name: 'KpiAdmin',
  computed: {
    moment() {
      return moment;
    },
  },
  data() {
    return {
      storeEmail: '',
      kpiData: null,
      loading: false,
      error: null,
    };
  },
  methods: {
    async fetchKpiData() {
      if (!this.storeEmail) {
        this.error = "Veuillez saisir l'email du store";
        return;
      }

      this.loading = true;
      this.error = null;

      try {
        this.kpiData = await APIAdmin.getKpi({ owner_email: this.storeEmail });
      } catch (error) {
        this.error = error.response?.data?.message || 'Une erreur est survenue lors du chargement des données';
      } finally {
        this.loading = false;
      }
    },
    formatDuration(minutes) {
      if (!minutes) return '0h 0min';
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}min`;
    },
  },
};
</script>

<style scoped>
.kpi-admin {
  padding: 20px;
}

.box {
  margin-bottom: 1.5rem;
}

.table {
  margin-bottom: 0;
}

.notification {
  margin-bottom: 1.5rem;
}
</style>
